<template>
  <div>
    <TheListHeader :title="t(pageTitle.title)" />
    <div class="mb-6 flex flex-wrap items-center gap-2 px-6 sm:px-12">
      <ThePageSettings
        :display-store-selector="stores.length > 1"
        :display-filters="true"
        @store-selected="changeStore"
        @validate-filters="updateFilters"
      >
        <template #filtersDescription>
          {{ $t('pages.review.reviewPage.filters.description') }}
        </template>
        <template #filtersContent>
          <fieldset>
            <legend class="mb-4 text-base font-medium text-foreground">
              {{ $t('pages.review.reviewPage.filters.platforms') }}
            </legend>

            <AppCheckboxField
              v-for="platform in platforms"
              :id="`platforms[${platform.slug}]`"
              :key="platform"
              v-model="selected.platforms"
              :value="platform.slug"
              class="my-2"
              :label="platform.name"
              @update:model-value="changeFilters"
            />
          </fieldset>

          <fieldset class="mt-8">
            <legend class="mb-4 text-base font-medium text-foreground">
              {{ $t('pages.review.reviewPage.filters.notes') }}
            </legend>
            <AppCheckboxField
              v-for="note in 5"
              :id="`notes[${note}]`"
              :key="note"
              v-model="selected.notes"
              :value="note"
              class="my-2"
              @update:model-value="changeFilters"
            >
              <template #label>
                <div class="flex w-full items-center md:col-span-1">
                  <svg
                    v-for="i in Number(note)"
                    :key="i"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="h-4 w-4 text-yellow-300"
                  >
                    <path
                      d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                    />
                  </svg>
                </div>
              </template>
            </AppCheckboxField>
          </fieldset>

          <fieldset class="mt-8">
            <legend class="mb-4 text-base font-medium text-foreground">
              {{ $t('pages.review.reviewPage.filters.recommend') }}
            </legend>
            <AppCheckboxField
              v-for="hasRecommend in hasRecommended"
              :id="`recommend[${hasRecommend}]`"
              :key="hasRecommend"
              v-model="selected.hasRecommended"
              :value="hasRecommend"
              class="my-2"
              @update:model-value="changeFilters"
            >
              <template #label>
                <div class="flex w-full items-center md:col-span-1">
                  <div v-if="hasRecommend === '1'" class="flex">
                    <BaseIcon name="HeroChatBubbleBottomCenterTextIconSolid" class="mr-1 w-4 text-red-400" />
                    <p class="text-xs text-foreground-secondary">
                      {{ $t('pages.review.reviewPage.items.recommend') }}
                    </p>
                  </div>
                  <div v-else class="flex">
                    <BaseIcon name="HeroChatBubbleBottomCenterTextIconOutline" class="mr-1 w-4 text-muted-foreground" />
                    <p class="text-xs text-foreground-secondary">
                      {{ $t('pages.review.reviewPage.items.noRecommend') }}
                    </p>
                  </div>
                </div>
              </template>
            </AppCheckboxField>
          </fieldset>

          <fieldset class="mt-8">
            <legend class="mb-4 text-base font-medium text-foreground">
              {{ $t('pages.review.reviewPage.filters.answer') }}
            </legend>
            <AppCheckboxField
              v-for="hasAnswer in hasAnswered"
              :id="`answer[${hasAnswer}]`"
              :key="hasAnswer"
              v-model="selected.hasAnswered"
              :value="hasAnswer"
              class="my-2"
              @update:model-value="changeFilters"
            >
              <template #label>
                <div class="flex w-full items-center md:col-span-1">
                  <div v-if="hasAnswer === '1'" class="flex">
                    <p class="text-xs text-foreground-secondary">
                      {{ $t('pages.review.reviewPage.items.replied') }}
                    </p>
                  </div>
                  <div v-else class="flex">
                    <p class="text-xs text-foreground-secondary">
                      {{ $t('pages.review.reviewPage.items.unreplied') }}
                    </p>
                  </div>
                </div>
              </template>
            </AppCheckboxField>
          </fieldset>
        </template>
      </ThePageSettings>
      <div>
        <Button
          as-child
          variant="default-primary"
          class="fixed bottom-0 left-0 z-10 w-screen rounded-none sm:static sm:w-auto sm:rounded-control"
        >
          <router-link :to="{ name: 'AutopilotList', hash: '#reviews' }">
            <PlusCircle class="animate-bounce" />
            {{ $t('pages.review.reviewPage.cards.getMoreReviews') }}
          </router-link>
        </Button>
      </div>
    </div>
    <div v-if="isLoaded">
      <div class="px-6 pb-6 sm:px-12 sm:pb-12">
        <UpsellModuleBanner />

        <div v-if="reviews.length > 0">
          <BaseTip v-if="!isReviewFetchingInRealTime" severity="info" class="mb-6">
            <template #text>
              <p>{{ $t('pages.review.reviewPage.notRealTime.disclaimer') }}</p>
            </template>
          </BaseTip>
          <div class="mt-6 sm:overflow-hidden">
            <div>
              <h3 class="text-lg font-semibold leading-6 text-foreground">
                {{
                  $t('pages.review.reviewPage.blocks.reviewsItems.title', {
                    countFiltered: countFiltered,
                    total: pagination.total
                  })
                }}
              </h3>
            </div>
            <ReviewList :reviews="reviews" :pagination="pagination" @update:change-page="changePage" />
          </div>
        </div>
        <div v-else-if="displayNoResults">
          <TheEmptyState
            module="review"
            :primary-button="emptyStateNoResultButton"
            :title="$t('pages.review.emptyState.noResult')"
            @on-click="handleClickEmptyState"
          />
        </div>
        <TheEmptyState
          v-else
          module="review"
          :primary-button="emptyStateButton"
          :title="$t('pages.review.emptyState.title')"
          :description="$t('pages.review.emptyState.description')"
          @on-click="$router.push({ name: 'ReviewSettings' })"
        />
      </div>
    </div>
    <TheLoadingPage v-else />
  </div>
</template>

<script>
import { Button } from '@/ui/button'
import { PlusCircle } from 'lucide-vue-next'
import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import TheEmptyState from '@/components/layout/TheEmptyState.vue'
import ThePageSettings from '@/components/layout/ThePageSettings.vue'
import ReviewList from './ReviewPage/ReviewList.vue'
import AppCheckboxField from '@/components/ui/AppCheckboxField.vue'
import useLocalStorage from '@/composables/useLocalStorage'
import useStore from '@/composables/store'
import { useRouter } from 'vue-router'

import BaseTip from '@/components/base/BaseTip.vue'
import UpsellModuleBanner from '@/components/UpsellModuleBanner.vue'

const defaultSelected = {
  platforms: [],
  notes: [],
  hasRecommended: [],
  hasAnswered: []
}

export default {
  components: {
    Button,
    PlusCircle,
    TheEmptyState,
    ThePageSettings,
    TheLoadingPage,
    ReviewList,
    AppCheckboxField,
    BaseTip,
    UpsellModuleBanner
  },
  setup() {
    const { isSupportConnection, permissions, stores, moduleAccessesByStores } = useLocalStorage()
    const { selectedStore, selectStore } = useStore()
    const { t } = useI18n()
    const router = useRouter()

    const emptyStateButton = {
      text: t('pages.review.emptyState.button'),
      icon: 'HeroPlusIconSolid',
      isOutlined: false
    }

    const emptyStateNoResultButton = {
      text: t(`pages.customer.customerPage.noResults.reset`),
      icon: 'HeroAdjustmentsVerticalIconSolid',
      isOutlined: false
    }

    return {
      isSupportConnection,
      permissions,
      selectedStore,
      selectStore,
      stores,
      moduleAccessesByStores,
      emptyStateNoResultButton,
      emptyStateButton,
      router,
      t
    }
  },
  data: () => {
    return {
      isLoaded: false,
      countFiltered: null,
      platforms: [],
      notes: ['1', '2', '3', '4', '5'],
      hasRecommended: ['0', '1'],
      hasAnswered: ['0', '1'],
      defaultSelected: defaultSelected,
      currentSelected: defaultSelected,
      selected: JSON.parse(JSON.stringify(defaultSelected)), // Create a non reactive copy
      pageTitle: {
        title: 'pages.review.reviewPage.title',
        description: 'pages.review.reviewPage.description'
      },
      pagination: {
        currentPage: 1,
        lastPage: null,
        total: null
      },
      reviews: [],
      isReviewFetchingInRealTime: false
    }
  },
  computed: {
    filtered() {
      return JSON.stringify(this.currentSelected) != JSON.stringify(this.defaultSelected)
    },
    displayNoResults() {
      return this.reviews.length == 0 && this.filtered
    },
    crumbs() {
      return [
        {
          name: this.$t('pages.review.reviewPage.title')
        }
      ]
    },
    notesToNumbers() {
      return this.selected['notes'].map((i) => Number(i))
    },
    hasRecommendedToNumbers() {
      return this.selected['hasRecommended'].map((i) => Number(i))
    },
    hasAnsweredToNumbers() {
      return this.selected['hasAnswered'].map((i) => Number(i))
    },
    storesWithEReputation() {
      return this.stores.filter((store) => this.moduleAccessesByStores[store.id]?.includes('e-reputation'))
    }
  },
  async created() {
    const { data } = await this.axios.get('/review-platforms')
    data.data.forEach((platform) => {
      this.defaultSelected.platforms.push(platform.slug)
    })
    this.platforms = data.data
    await this.fetchReviews()
    this.isLoaded = true
  },
  methods: {
    changePage(page) {
      this.pagination.currentPage = page
      this.fetchReviews()
    },
    changeStore(id) {
      if (this.storesWithEReputation.some((store) => store.id === id)) {
        this.selectStore(0)
        this.router.push({ name: 'EReputation', params: { page: 'reviews' } })
        return
      }

      this.pagination.currentPage = 1
      this.fetchReviews()
    },
    changeFilters() {
      this.pagination.currentPage = 1
    },
    updateFilters() {
      this.currentSelected = JSON.parse(JSON.stringify(this.selected))
      this.fetchReviews()
    },
    fetchReviews() {
      this.isLoaded = false
      return this.axios
        .get('/reviews', {
          params: {
            storeIds: this.selectedStore,
            platformSlugs: this.selected.platforms.join(','),
            notes: this.selected.notes.join(','),
            hasRecommended: this.selected.hasRecommended.length > 1 ? null : this.selected.hasRecommended[0],
            hasAnswered: this.selected.hasAnswered.length > 1 ? null : this.selected.hasAnswered[0],
            page: this.pagination.currentPage,
            pageSize: 10
          }
        })
        .then((response) => {
          this.pagination.total = response.data.meta.total
          this.pagination.lastPage = response.data.meta.last_page
          this.reviews = response.data.data
          this.isReviewFetchingInRealTime = response.data.meta.is_review_fetching_in_real_time
        })
        .catch((error) => {
          let status = error.response.status
          if (status === 500) {
            //displayNoResult
            this.reviews = []
          }
        })
        .finally(() => (this.isLoaded = true))
    },
    async handleClickEmptyState(event) {
      if (event === 'primaryButton') {
        this.selected = JSON.parse(JSON.stringify(defaultSelected))
        this.currentSelected = this.defaultSelected
        await this.fetchReviews()
      }
    }
  }
}
</script>
